import { spiColors } from '@src/utils/kpi/tools';
import type { AppFlowMetadata } from '@src/hooks/flow/useFlowConfigs';
import { FLOW_PRESET_DEFAULT_DIMENSIONS } from '@src/utils/dimension/appDimensionHelper';
import { getInvertedPercentageMetricByKey } from '@src/utils/metrics/metricHelpers';
import { getFlowMetrics } from '@utils/flow/utils';

export const getTextColor = (value: number, thresholds: any) => {
  if (value < thresholds.low) {
    return spiColors.low;
  }
  if (value >= thresholds.low && value < thresholds.medium) {
    return spiColors.medium;
  }
  return spiColors.high;
};

export const getThresholds = (flowSettings) => {
  const { threshold } = flowSettings;
  return {
    scaled: {
      low: threshold.t1 ? Number(threshold.t1) / 100 : 0,
      medium: threshold.t2 ? Number(threshold.t2) / 100 : 0,
      high: 1,
    },
    absolute: {
      low: threshold.t1 ? Number(threshold.t1) : 0,
      medium: threshold.t2 ? Number(threshold.t2) : 0,
      high: 100,
    },
  };
};

export const formatGaugeChartData = (spiScore, thresholds) => {
  const option = {
    series: [
      {
        type: 'gauge',
        startAngle: 180,
        endAngle: 0,
        radius: '100%',
        center: ['50%', '70%'],
        min: 0,
        max: 100,
        splitNumber: 0,
        axisLine: {
          lineStyle: {
            width: 5,
            color: [
              [thresholds.low, spiColors.low],
              [thresholds.medium, spiColors.medium],
              [thresholds.high, spiColors.high],
            ],
          },
        },
        pointer: {
          icon: 'path://M12.8,0.7l12,40.1H0.7L12.8,0.7z',
          length: '50%',
          width: 3,
          offsetCenter: [0, '-15%'],
          itemStyle: {
            color: 'auto',
          },
        },
        axisTick: {
          length: 1,
          lineStyle: {
            color: 'auto',
            width: 5,
          },
        },
        splitLine: {
          length: 20,
          lineStyle: {
            color: 'auto',
            width: 1,
          },
          show: false,
        },
        axisLabel: {
          fontSize: 20,
          distance: -60,
          formatter() {
            return '';
          },
        },
        title: {
          offsetCenter: [0, '-20%'],
          fontSize: 30,
        },
        detail: {
          fontSize: 50,
          offsetCenter: [40, '5%'],
          valueAnimation: true,
          formatter() {
            return '';
          },
        },
        data: [
          {
            value: spiScore,
          },
        ],
      },
    ],
  };

  return option;
};

// NOTE: this function is reused in Nexa Dashboard Link generation for flows
// temp cross import, should be moved to a common flow utils file
export const getFlowDrilldownMetrics = (flowMeta: AppFlowMetadata) => {
  if (!flowMeta || !flowMeta.metricsMeta) return [];

  const drilldownMetrics = getFlowMetrics(flowMeta).sort((a, b) => {
    const ma = getInvertedPercentageMetricByKey({
      metricKey: a,
      defaultValue: a,
    });
    const mb = getInvertedPercentageMetricByKey({
      metricKey: b,
      defaultValue: b,
    });
    const maOrder = flowMeta.metricsOrder[ma] ?? Infinity;
    const mbOrder = flowMeta.metricsOrder[mb] ?? Infinity;

    return maOrder - mbOrder;
  });

  return drilldownMetrics;
};

export const genFlowCompatibleDimensions = (
  dimensions: string[],
  flowAssociatedDimensions: Set<string>,
) => {
  const convertedDimensions = dimensions
    .map((it) => {
      if (flowAssociatedDimensions.has(it)) return it;

      return undefined;
    })
    .filter(Boolean) as string[];

  if (!convertedDimensions.length) return FLOW_PRESET_DEFAULT_DIMENSIONS;

  return convertedDimensions;
};

export const genFlowCompatibleFilter = (filter: any, flowAssociatedDimensions: Set<string>) => {
  let isChanged = false;
  const convertedRules = filter.rule
    .map((it) =>
      it
        .map((item) => {
          if (flowAssociatedDimensions.has(item.field)) return item;

          isChanged = true;

          return undefined;
        })
        .filter(Boolean),
    )
    .filter((it) => it.length);

  if (!isChanged) return filter;

  return {
    ...filter,
    fid: undefined,
    name: undefined,
    rule: convertedRules,
  };
};

export const getFlowType = (flow) => {
  if (flow?.definition?.flow_sql_definition?.length) return 'pattern';
  return 'flow';
};
